import React, { useEffect, useState } from 'react';
import { Button, IconButton, Box, MenuItem, Paper, Typography, Tooltip, Divider } from '@mui/material';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import { ReactComponent as DeleteIcon } from '../assets/images/deleteRedIcon.svg';
import {
    CustomerBorderedInput,
    CustomerBorderedSelect,
    CustomerFieldLabel,
} from '../utils/customerFormUi.js';

function CenteredHeaderLabel({ label, required }) {
    return (
        <CustomerFieldLabel label={label} required={required} centered />
    );
}

function OrderColumnHeaders({ onSelectAll }) {
    return (
        <Box className="customer-form-order-header">
            <Box className="customer-form-order-header-col customer-form-order-header-col--select">
                <Button
                    type="button"
                    size="small"
                    onClick={onSelectAll}
                    className="customer-form-select-all-btn"
                >
                    Select all
                </Button>
            </Box>
            <Box className="customer-form-order-header-col customer-form-order-header-col--type">
                <CenteredHeaderLabel label="Type" required />
            </Box>
            <Box className="customer-form-order-header-col customer-form-order-header-col--size">
                <CenteredHeaderLabel label="Size" required />
            </Box>
            <Box className="customer-form-order-header-col customer-form-order-header-col--qty">
                <CenteredHeaderLabel label="Quantity" required />
            </Box>
            <Box className="customer-form-order-header-col customer-form-order-header-col--tracking">
                <CenteredHeaderLabel label="Tracking ID" required />
            </Box>
            <Box className="customer-form-order-header-col customer-form-order-header-col--actions">
                <Typography component="label" className="customer-form-field-label customer-form-field-label--center">
                    Actions
                </Typography>
            </Box>
        </Box>
    );
}

const DynamicForm = (props) => {
    const [duplicate, setDuplicate] = useState([]);
    const [rows, setRows] = useState([
        ...[1, 2].map(() => ({
            id: Date.now() + Math.random(),
            tracking_id: '',
            products: [{ id: Date.now() + Math.random(), product_type: '', size: '', quantity: '' }],
        })),
    ]);

    const shoeTypeOptions = [
        { name: 'B', label: 'Long Length(B)' },
        { name: 'SB', label: 'Short Length(SB)' },
        { name: 'FG', label: 'Fishing Shoes Soft(FG)' },
        { name: '2FG', label: 'Fishing Shoes Strong(2FG)' },
        { name: 'OR', label: 'Orange Shoe(OR)' },
        { name: 'WF', label: 'Waders(WF)' },
        { name: 'PR', label: 'Poorner(PR)' },
    ];

    const productSizes = [
        { value: 36, size: '3/36' },
        { value: 37, size: '4/37' },
        { value: 38, size: '5/38' },
        { value: 39, size: '6/39' },
        { value: 40, size: '7/40' },
        { value: 41, size: '8/41' },
        { value: 42, size: '9/42' },
        { value: 43, size: '10/43' },
        { value: 44, size: '11/44' },
        { value: 45, size: '12/45' },
        { value: 46, size: '13/46' },
        { value: 0, size: 'free' },
    ];

    const FishingSizes = [
        { value: 36, size: '2/36' },
        { value: 37, size: '3/37' },
        { value: 38, size: '4/38' },
        { value: 39, size: '5/39' },
        { value: 40, size: '6/40' },
        { value: 41, size: '7/41' },
        { value: 42, size: '8/42' },
        { value: 43, size: '9/43' },
        { value: 44, size: '10/44' },
        { value: 45, size: '11/45' },
        { value: 46, size: '12/46' },
    ];

    const productORSizes = [
        { value: 1, size: '5/1' },
        { value: 2, size: '6/2' },
        { value: 3, size: '7/3' },
        { value: 4, size: '8/4' },
        { value: 5, size: '9/5' },
        { value: 6, size: '10/6' },
        { value: 7, size: '11/7' },
        { value: 8, size: '12/8' },
        { value: 9, size: '13/9' },
        { value: 10, size: '14/10' },
        { value: 11, size: '15/11' },
        { value: 12, size: '16/12' },
    ];

    const getSizeOptions = (productType) => {
        if (productType === 'FG' || productType === '2FG' || productType === 'WF') {
            return FishingSizes;
        }
        if (productType === 'OR') {
            return productORSizes;
        }
        return productSizes;
    };

    useEffect(() => {
        if (props?.sizeDetails?.length) {
            setRows(props?.sizeDetails);
        }
    }, [props?.sizeDetails]);

    useEffect(() => {
        if (props?.clearForm) {
            setRows([
                ...[1, 2].map(() => ({
                    id: Date.now() + Math.random(),
                    tracking_id: '',
                    products: [{ id: Date.now() + Math.random(), product_type: '', size: '', quantity: '' }],
                })),
            ]);
            props?.setClearForm(false);
        }
    }, [props?.clearForm]);

    const handleChange = (rowIndex, productId, field, value) => {
        const updatedRows = rows.map((row, index) => {
            if (index !== rowIndex) return row;
            return {
                ...row,
                products: row.products.map((product) => {
                    if (product.id !== productId) return product;
                    const updatedProduct = { ...product, [field]: value };
                    if (field === 'product_type') {
                        updatedProduct.size = '';
                    }
                    return updatedProduct;
                }),
            };
        });
        setRows(updatedRows);
        props?.setSizeDetails(updatedRows);
    };

    const handleTrackingIdChange = (rowIndex, value) => {
        const updatedRows = rows.map((row, index) => {
            if (index !== rowIndex) return row;
            return { ...row, tracking_id: value };
        });
        setRows(updatedRows);
        props?.setSizeDetails(updatedRows);

        const trackingIds = updatedRows.map((row) => row.tracking_id?.trim()).filter(Boolean);
        const duplicates = trackingIds.filter((id, index) => trackingIds.indexOf(id) !== index);
        setDuplicate([...new Set(duplicates)]);
    };

    const handleTrackingIdKeyDown = (e, rowIndex) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault();
            const nextIndex = rowIndex + 1;
            if (nextIndex < rows.length) {
                const nextInput = document.querySelector(`[data-tracking-index="${nextIndex}"]`);
                nextInput?.focus();
            }
        }
    };

    const handleAddRow = () => {
        const newRow = {
            id: Date.now() + Math.random(),
            tracking_id: '',
            products: [{ id: Date.now() + Math.random(), product_type: '', size: '', quantity: '' }],
        };
        const updatedRows = [...rows, newRow];
        setRows(updatedRows);
        props?.setSizeDetails(updatedRows);
    };

    const handleDeleteRow = (rowId) => {
        if (rows.length <= 1) return;
        const updatedRows = rows.filter((row) => row.id !== rowId);
        setRows(updatedRows);
        props?.setSizeDetails(updatedRows);
    };

    const handleAddProduct = (rowIndex) => {
        const updatedRows = rows.map((row, index) => {
            if (index !== rowIndex) return row;
            return {
                ...row,
                products: [
                    ...row.products,
                    { id: Date.now() + Math.random(), product_type: '', size: '', quantity: '' },
                ],
            };
        });
        setRows(updatedRows);
        props?.setSizeDetails(updatedRows);
    };

    const handleDeleteProduct = (rowIndex, productId, products) => {
        if (products.length <= 1) return;
        const updatedRows = rows.map((row, index) => {
            if (index !== rowIndex) return row;
            return {
                ...row,
                products: row.products.filter((product) => product.id !== productId),
            };
        });
        setRows(updatedRows);
        props?.setSizeDetails(updatedRows);
    };

    const isProductFilled = (product) =>
        Boolean(
            product?.product_type &&
            product.product_type !== 'default' &&
            product?.size !== '' &&
            product?.size != null &&
            product?.quantity !== '' &&
            product?.quantity != null
        );

    const isProductEmpty = (product) =>
        !product?.product_type &&
        (product?.size === '' || product?.size == null) &&
        (product?.quantity === '' || product?.quantity == null);

    const handleSelectAll = () => {
        let sourceIndex = -1;
        let sourceProduct = null;

        for (let i = 0; i < rows.length; i++) {
            const firstProduct = rows[i]?.products?.[0];
            if (isProductFilled(firstProduct)) {
                sourceIndex = i;
                sourceProduct = firstProduct;
            }
        }

        if (sourceIndex < 0 || !sourceProduct) return;

        const updatedRows = rows.map((row, index) => {
            if (index <= sourceIndex) return row;

            const firstProduct = row.products?.[0];
            if (!firstProduct || !isProductEmpty(firstProduct)) return row;

            return {
                ...row,
                products: row.products.map((product, productIndex) =>
                    productIndex === 0
                        ? {
                            ...product,
                            product_type: sourceProduct.product_type,
                            quantity: sourceProduct.quantity,
                        }
                        : product
                ),
            };
        });

        setRows(updatedRows);
        props?.setSizeDetails(updatedRows);
    };

    return (
        <Paper elevation={0} className="customer-form-section">
            <Box className="customer-form-section-header">
                <Box className="customer-form-icon-badge">
                    <LocalShippingOutlinedIcon className="customer-form-icon" />
                </Box>
                <Box>
                    <Typography className="customer-form-section-title">
                        Marketplace Order Details
                    </Typography>
                    <Typography className="customer-form-section-subtitle">
                        Enter tracking IDs and product type, size, and quantity for each shipment.
                    </Typography>
                </Box>
            </Box>

            <OrderColumnHeaders onSelectAll={handleSelectAll} />

            {rows.map((row, index) => (
                <Box key={row.id} className="customer-form-order-card">
                    {row?.products?.map((product, productIndex) => (
                        <Box key={product.id} className="customer-form-order-row">
                            <Box className="customer-form-order-index-col">
                                {productIndex === 0 ? (
                                    <Box className="customer-form-order-index-badge">
                                        {index + 1}
                                    </Box>
                                ) : null}
                            </Box>

                            <Box className="customer-form-order-field customer-form-order-field--type">
                                <Box className="customer-form-mobile-label">
                                    <CustomerFieldLabel label="Type" required />
                                </Box>
                                <CustomerBorderedSelect
                                    value={product.product_type || 'default'}
                                    onChange={(e) => handleChange(index, product.id, 'product_type', e.target.value)}
                                >
                                    <MenuItem key="default" value="default" disabled>
                                        --Select--
                                    </MenuItem>
                                    {shoeTypeOptions.map((oppor) => (
                                        <MenuItem key={oppor.name} value={oppor.name}>
                                            {oppor.label}
                                        </MenuItem>
                                    ))}
                                </CustomerBorderedSelect>
                            </Box>

                            <Box className="customer-form-order-field customer-form-order-field--size">
                                <Box className="customer-form-mobile-label">
                                    <CustomerFieldLabel label="Size" required />
                                </Box>
                                <CustomerBorderedSelect
                                    value={product.size || 'default'}
                                    onChange={(e) => handleChange(index, product.id, 'size', e.target.value)}
                                >
                                    <MenuItem key="default" value="default" disabled>
                                        --Select--
                                    </MenuItem>
                                    {getSizeOptions(product.product_type)?.map((size) => (
                                        <MenuItem key={size.value} value={size.value}>
                                            {size.size}
                                        </MenuItem>
                                    ))}
                                </CustomerBorderedSelect>
                            </Box>

                            <Box className="customer-form-order-field customer-form-order-field--qty">
                                <Box className="customer-form-mobile-label">
                                    <CustomerFieldLabel label="Quantity" required />
                                </Box>
                                <CustomerBorderedInput
                                    placeholder="Qty"
                                    value={product.quantity}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value >= 0) {
                                            handleChange(index, product.id, 'quantity', value);
                                        }
                                    }}
                                    inputProps={{ autoComplete: 'off', inputMode: 'numeric' }}
                                />
                            </Box>

                            {productIndex === 0 ? (
                                <Box className="customer-form-order-field customer-form-order-field--tracking">
                                    <Box className="customer-form-mobile-label">
                                        <CustomerFieldLabel label="Tracking ID" required />
                                    </Box>
                                    <CustomerBorderedInput
                                        icon={QrCodeScannerOutlinedIcon}
                                        placeholder="Enter tracking number"
                                        value={row.tracking_id}
                                        onChange={(e) => handleTrackingIdChange(index, e.target.value)}
                                        onKeyDown={(e) => handleTrackingIdKeyDown(e, index)}
                                        error={duplicate.includes(row.tracking_id)}
                                        inputProps={{ 'data-tracking-index': index }}
                                    />
                                    {duplicate.includes(row.tracking_id) ? (
                                        <Typography className="danger-color error-msg customer-form-field-error">
                                            Tracking ID is duplicate
                                        </Typography>
                                    ) : null}
                                </Box>
                            ) : (
                                <Box className="customer-form-order-tracking-spacer" />
                            )}

                            <Box className="customer-form-order-actions">
                                {productIndex === 0 ? (
                                    <>
                                        <Tooltip title="Add another product to this shipment" enterTouchDelay={0}>
                                            <Button
                                                type="button"
                                                size="small"
                                                onClick={() => handleAddProduct(index)}
                                                startIcon={<PlaylistAddOutlinedIcon className="customer-form-order-action-icon" />}
                                                className="customer-form-order-action-btn customer-form-order-action-btn--item"
                                            >
                                                Item
                                            </Button>
                                        </Tooltip>
                                        <Divider orientation="vertical" flexItem className="customer-form-order-action-divider" />
                                        <Tooltip title="Add a new shipment row" enterTouchDelay={0}>
                                            <Button
                                                type="button"
                                                size="small"
                                                onClick={handleAddRow}
                                                startIcon={<PostAddOutlinedIcon className="customer-form-order-action-icon" />}
                                                className="customer-form-order-action-btn customer-form-order-action-btn--order"
                                            >
                                                Order
                                            </Button>
                                        </Tooltip>
                                        <Tooltip title="Remove this shipment row" enterTouchDelay={0}>
                                            <IconButton
                                                onClick={() => handleDeleteRow(row.id)}
                                                aria-label="Remove shipment row"
                                                disabled={rows.length <= 1}
                                                className="customer-form-action-icon customer-form-action-icon--delete"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <Tooltip title="Remove this product" enterTouchDelay={0}>
                                        <IconButton
                                            onClick={() => handleDeleteProduct(index, product.id, row?.products)}
                                            aria-label="Remove product"
                                            className="customer-form-action-icon customer-form-action-icon--delete"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>
            ))}
        </Paper>
    );
};

export default DynamicForm;
